// src/pages/reporteria/financiero/IngresosVentas.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Space,
  Statistic,
  Table,
  Tabs,
  Typography,
  Tag,
  Spin,
  Alert,
  Divider,
  Empty
} from 'antd';
import {
  DollarOutlined,
  DownloadOutlined,
  RiseOutlined,
  FallOutlined,
  LineChartOutlined,
  ShopOutlined,
  CalendarOutlined,
  BarChartOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import ReporteriaService from '../../../../service/Reporteria';
import useAuthContext from '../../../../hooks/useAuthContext';
import { exportarLibro } from '../../../../utils/exportExcel';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Extender dayjs
dayjs.extend(weekOfYear);

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const { RangePicker } = DatePicker;
const { Title: AntTitle, Text } = Typography;
const { Option } = Select;

const IngresosVentas = () => {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [fechas, setFechas] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [agruparPor, setAgruparPor] = useState('dia');
  const [activeTab, setActiveTab] = useState('resumen');

  // Estados de datos
  const [ingresosPorPeriodo, setIngresosPorPeriodo] = useState(null);
  const [ingresosPorTaller, setIngresosPorTaller] = useState(null);
  const [ingresosPorRecinto, setIngresosPorRecinto] = useState(null);
  const [ingresosPorPlan, setIngresosPorPlan] = useState(null);

  // Cargar datos
  const cargarDatos = async () => {
    setLoading(true);
    try {
      const params = {
        fechaInicio: fechas[0].format('YYYY-MM-DD'),
        fechaFin: fechas[1].format('YYYY-MM-DD')
      };

      const [periodo, talleres, recintos, planes] = await Promise.all([
        ReporteriaService.GetIngresosPorPeriodo({ ...params, agruparPor }),
        ReporteriaService.GetIngresosPorTaller({ ...params, limite: 20 }),
        ReporteriaService.GetIngresosPorRecinto(params),
        ReporteriaService.GetIngresosPorTipoPlan(params)
      ]);

      setIngresosPorPeriodo(periodo);
      setIngresosPorTaller(talleres);
      setIngresosPorRecinto(recintos);
      setIngresosPorPlan(planes);

    } catch (error) {
      console.error('Error cargando datos de ingresos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [fechas, agruparPor]);

  // Formatear moneda
  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(monto || 0);
  };

  // Preparar datos para gráfico de línea (tendencia)
  const prepararDatosTendencia = () => {
    if (!ingresosPorPeriodo?.desglosePorPeriodo) return null;

    const labels = ingresosPorPeriodo.desglosePorPeriodo.map(item => {
      const fecha = dayjs(item._id);
      if (agruparPor === 'dia') return fecha.format('DD/MM');
      if (agruparPor === 'semana') return `Sem ${fecha.week()}`;
      if (agruparPor === 'mes') return fecha.format('MMM YYYY');
      return item._id;
    });

    return {
      labels,
      datasets: [
        {
          label: 'Ingresos',
          data: ingresosPorPeriodo.desglosePorPeriodo.map(item => item.totalIngresos),
          borderColor: '#0047bb',
          backgroundColor: 'rgba(0, 71, 187, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: 'Descuentos',
          data: ingresosPorPeriodo.desglosePorPeriodo.map(item => item.totalDescuentos),
          borderColor: '#ff4d4f',
          backgroundColor: 'rgba(255, 77, 79, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
  };

  // Preparar datos para gráfico de barras (talleres)
  const prepararDatosTalleres = () => {
    if (!ingresosPorTaller?.topTalleres) return null;

    const top10 = ingresosPorTaller.topTalleres.slice(0, 10);

    return {
      labels: top10.map(t => t.tallerNombre),
      datasets: [
        {
          label: 'Ingresos',
          data: top10.map(t => t.totalIngresos),
          backgroundColor: '#0047bb',
          borderRadius: 8
        }
      ]
    };
  };

  // Preparar datos para gráfico de pie (planes)
  const prepararDatosPlanes = () => {
    if (!ingresosPorPlan?.distribucion) return null;

    const colores = {
      gratuito: '#d9d9d9',
      mensual: '#0047bb',
      trimestral: '#0047bb',
      semestral: '#722ed1',
      anual: '#faad14'
    };

    return {
      labels: ingresosPorPlan.distribucion.map(p => p.plan),
      datasets: [
        {
          data: ingresosPorPlan.distribucion.map(p => p.totalIngresos),
          backgroundColor: ingresosPorPlan.distribucion.map(
            p => colores[p.plan] || '#8c8c8c'
          ),
          borderWidth: 2,
          borderColor: '#fff'
        }
      ]
    };
  };

  // Opciones para gráficos
  const opcionesGraficoLinea = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatearMoneda(context.parsed.y)}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatearMoneda(value)
        }
      }
    }
  };

  const opcionesGraficoBarra = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => formatearMoneda(context.parsed.x)
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatearMoneda(value)
        }
      }
    }
  };

  const opcionesGraficoPie = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = formatearMoneda(context.parsed);
            return `${label}: ${value}`;
          }
        }
      }
    }
  };

  // Columnas para tabla de talleres
  const columnasTalleres = [
    {
      title: '#',
      key: 'ranking',
      width: 60,
      render: (_, __, index) => (
        <Tag color={index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? '#cd7f32' : 'default'}>
          {index + 1}
        </Tag>
      )
    },
    {
      title: 'Taller',
      dataIndex: 'tallerNombre',
      key: 'tallerNombre',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" className="text-xs">{record.recintoNombre}</Text>
        </div>
      )
    },
    {
      title: 'Inscripciones',
      dataIndex: 'totalInscripciones',
      key: 'totalInscripciones',
      align: 'center',
      sorter: (a, b) => a.totalInscripciones - b.totalInscripciones
    },
    {
      title: 'Ingresos',
      dataIndex: 'totalIngresos',
      key: 'totalIngresos',
      align: 'right',
      render: (value) => <Text strong style={{ color: '#0047bb' }}>{formatearMoneda(value)}</Text>,
      sorter: (a, b) => a.totalIngresos - b.totalIngresos,
      defaultSortOrder: 'descend'
    },
    {
      title: 'Descuentos',
      dataIndex: 'totalDescuentos',
      key: 'totalDescuentos',
      align: 'right',
      render: (value) => <Text type="danger">{formatearMoneda(value)}</Text>,
      sorter: (a, b) => a.totalDescuentos - b.totalDescuentos
    },
    {
      title: 'Promedio',
      dataIndex: 'ingresoPromedioPorInscrito',
      key: 'ingresoPromedioPorInscrito',
      align: 'right',
      render: (value) => formatearMoneda(value),
      sorter: (a, b) => a.ingresoPromedioPorInscrito - b.ingresoPromedioPorInscrito
    }
  ];

  // Columnas para tabla de recintos
  const columnasRecintos = [
    {
      title: 'Recinto',
      dataIndex: 'recintoNombre',
      key: 'recintoNombre',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" className="text-xs">{record.recintoComuna}</Text>
        </div>
      )
    },
    {
      title: 'Talleres',
      dataIndex: 'cantidadTalleres',
      key: 'cantidadTalleres',
      align: 'center'
    },
    {
      title: 'Inscripciones',
      dataIndex: 'totalInscripciones',
      key: 'totalInscripciones',
      align: 'center',
      sorter: (a, b) => a.totalInscripciones - b.totalInscripciones
    },
    {
      title: 'Ingresos',
      dataIndex: 'totalIngresos',
      key: 'totalIngresos',
      align: 'right',
      render: (value) => <Text strong style={{ color: '#0047bb' }}>{formatearMoneda(value)}</Text>,
      sorter: (a, b) => a.totalIngresos - b.totalIngresos,
      defaultSortOrder: 'descend'
    },
    {
      title: 'Promedio por Inscripción',
      dataIndex: 'ingresoPromedio',
      key: 'ingresoPromedio',
      align: 'right',
      render: (value) => formatearMoneda(value),
      sorter: (a, b) => a.ingresoPromedio - b.ingresoPromedio
    }
  ];

  // Exportar a CSV
  const exportarCSV = (datos, nombreArchivo) => {
    try {
      ReporteriaService.ExportarReporteACSV(datos, nombreArchivo);
    } catch (error) {
      console.error('Error exportando CSV:', error);
    }
  };

  // Exportar a Excel (multi-hoja)
  const exportarExcel = async () => {
    try {
      const hojas = [];

      // Hoja 1: Resumen del período (formato ficha, no una fila horizontal)
      if (ingresosPorPeriodo?.totales) {
        const t = ingresosPorPeriodo.totales;
        hojas.push({
          nombre: 'Resumen',
          metricas: [
            { metrica: 'Período analizado' },
            { metrica: 'Desde', valor: fechas[0].toDate(), tipo: 'fecha' },
            { metrica: 'Hasta', valor: fechas[1].toDate(), tipo: 'fecha' },
            { metrica: 'Agrupación', valor: agruparPor },
            { metrica: 'Ingresos' },
            { metrica: 'Total bruto', valor: t.totalBruto || 0, tipo: 'moneda' },
            { metrica: 'Total descuentos', valor: t.totalDescuentos || 0, tipo: 'moneda' },
            { metrica: 'Total ingresos (neto)', valor: t.totalIngresos || 0, tipo: 'moneda' },
            { metrica: 'Operaciones' },
            { metrica: 'Cantidad de pagos', valor: t.cantidadPagos || 0, tipo: 'numero' },
            { metrica: 'Ticket promedio', valor: t.promedioTicket || 0, tipo: 'moneda' },
            {
              metrica: '% de descuento sobre bruto',
              valor: t.totalBruto ? (t.totalDescuentos / t.totalBruto) * 100 : 0,
              tipo: 'porcentaje',
            },
          ],
        });
      }

      // Hoja 2: Por Taller
      if (ingresosPorTaller?.topTalleres?.length) {
        hojas.push({
          nombre: 'Por taller',
          columnas: [
            { header: '#', key: 'ranking', tipo: 'numero', ancho: 6 },
            { header: 'Taller', key: 'taller', tipo: 'texto', ancho: 38 },
            { header: 'Recinto', key: 'recinto', tipo: 'texto', ancho: 26 },
            { header: 'Inscripciones', key: 'inscripciones', tipo: 'numero', total: true },
            { header: 'Ingresos', key: 'ingresos', tipo: 'moneda', total: true },
            { header: 'Descuentos', key: 'descuentos', tipo: 'moneda', total: true },
            { header: 'Promedio por inscrito', key: 'promedio', tipo: 'moneda' },
          ],
          filas: ingresosPorTaller.topTalleres.map((t, i) => ({
            ranking: i + 1,
            taller: t.tallerNombre,
            recinto: t.recintoNombre,
            inscripciones: t.totalInscripciones,
            ingresos: t.totalIngresos,
            descuentos: t.totalDescuentos,
            promedio: t.ingresoPromedioPorInscrito,
          })),
        });
      }

      // Hoja 3: Por Recinto
      if (ingresosPorRecinto?.length) {
        hojas.push({
          nombre: 'Por recinto',
          columnas: [
            { header: 'Recinto', key: 'recinto', tipo: 'texto', ancho: 30 },
            { header: 'Comuna', key: 'comuna', tipo: 'texto' },
            { header: 'Talleres', key: 'talleres', tipo: 'numero', total: true },
            { header: 'Inscripciones', key: 'inscripciones', tipo: 'numero', total: true },
            { header: 'Ingresos', key: 'ingresos', tipo: 'moneda', total: true },
            { header: 'Promedio por inscripción', key: 'promedio', tipo: 'moneda' },
          ],
          filas: ingresosPorRecinto.map(r => ({
            recinto: r.recintoNombre,
            comuna: r.recintoComuna,
            talleres: r.cantidadTalleres,
            inscripciones: r.totalInscripciones,
            ingresos: r.totalIngresos,
            promedio: r.ingresoPromedio,
          })),
        });
      }

      // Hoja 4: Por Plan
      if (ingresosPorPlan?.distribucion?.length) {
        hojas.push({
          nombre: 'Por plan',
          columnas: [
            { header: 'Plan', key: 'plan', tipo: 'texto', ancho: 26 },
            { header: 'Inscripciones', key: 'inscripciones', tipo: 'numero', total: true },
            { header: 'Ingresos', key: 'ingresos', tipo: 'moneda', total: true },
            { header: 'Promedio por inscripción', key: 'promedio', tipo: 'moneda' },
          ],
          filas: ingresosPorPlan.distribucion.map(p => ({
            plan: p.plan,
            inscripciones: p.cantidadInscripciones,
            ingresos: p.totalIngresos,
            promedio: p.ingresoPromedio,
          })),
        });
      }

      // Hoja 5: Serie temporal
      if (ingresosPorPeriodo?.desglosePorPeriodo?.length) {
        hojas.push({
          nombre: 'Serie temporal',
          columnas: [
            { header: 'Período', key: 'periodo', tipo: 'texto', ancho: 16 },
            { header: 'Ingresos', key: 'ingresos', tipo: 'moneda', total: true },
            { header: 'Descuentos', key: 'descuentos', tipo: 'moneda', total: true },
            { header: 'Cantidad de pagos', key: 'pagos', tipo: 'numero', total: true },
          ],
          filas: ingresosPorPeriodo.desglosePorPeriodo.map(item => ({
            periodo: item._id,
            ingresos: item.totalIngresos,
            descuentos: item.totalDescuentos,
            pagos: item.cantidadPagos,
          })),
          notas: [`Agrupación: por ${agruparPor}. El período se deja como texto para respetar la agrupación de la consulta.`],
        });
      }

      await exportarLibro({
        archivo: 'ingresos-ventas',
        titulo: 'Ingresos y ventas',
        subtitulo: `Reporte financiero · ${fechas[0].format('DD/MM/YYYY')} al ${fechas[1].format('DD/MM/YYYY')}`,
        usuario: user,
        filtros: {
          'Desde': fechas[0].format('DD/MM/YYYY'),
          'Hasta': fechas[1].format('DD/MM/YYYY'),
          'Agrupación': agruparPor,
        },
        hojas,
      });
    } catch (error) {
      console.error('Error exportando Excel:', error);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <AntTitle level={2} className="mb-2">
          <DollarOutlined className="mr-2" />
          Ingresos y Ventas
        </AntTitle>
        <Text type="secondary">
          Análisis detallado de ingresos por periodo, talleres y recintos
        </Text>
      </div>

      {/* Filtros */}
      <Card className="mb-6 shadow-sm">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12} lg={10}>
            <Space direction="vertical" className="w-full">
              <Text strong>Periodo:</Text>
              <RangePicker
                value={fechas}
                onChange={(dates) => dates && setFechas(dates)}
                format="DD/MM/YYYY"
                className="w-full"
                size="large"
              />
            </Space>
          </Col>

          <Col xs={24} md={8} lg={6}>
            <Space direction="vertical" className="w-full">
              <Text strong>Agrupar por:</Text>
              <Select
                value={agruparPor}
                onChange={setAgruparPor}
                className="w-full"
                size="large"
              >
                <Option value="dia">Día</Option>
                <Option value="semana">Semana</Option>
                <Option value="mes">Mes</Option>
              </Select>
            </Space>
          </Col>

          <Col xs={24} md={4} lg={4}>
            <Button
              type="primary"
              size="large"
              onClick={cargarDatos}
              loading={loading}
              className="w-full mt-6"
              style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
            >
              Actualizar
            </Button>
          </Col>

          <Col xs={24} md={4} lg={4}>
            <Button
              icon={<DownloadOutlined />}
              size="large"
              onClick={exportarExcel}
              disabled={loading || !ingresosPorPeriodo}
              className="w-full mt-6"
            >
              Exportar Excel
            </Button>
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Spin size="large" tip="Cargando datos..." />
        </div>
      ) : (
        <>
          {/* KPIs */}
          <Row gutter={[16, 16]} className="mb-6">
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm">
                <Statistic
                  title="Ingresos Totales"
                  value={ingresosPorPeriodo?.totales?.totalIngresos || 0}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#0047bb' }}
                  formatter={(value) => formatearMoneda(value)}
                />
                <Divider className="my-2" />
                <Text type="secondary" className="text-xs">
                  Bruto: {formatearMoneda(ingresosPorPeriodo?.totales?.totalBruto || 0)}
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm">
                <Statistic
                  title="Total Descuentos"
                  value={ingresosPorPeriodo?.totales?.totalDescuentos || 0}
                  prefix="$"
                  valueStyle={{ color: '#ff4d4f' }}
                />
                <Divider className="my-2" />
                <Text type="secondary" className="text-xs">
                  {ingresosPorPeriodo?.totales?.cantidadPagos || 0} pagos
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm">
                <Statistic
                  title="Ticket Promedio"
                  value={ingresosPorPeriodo?.totales?.promedioTicket || 0}
                  prefix="$"
                  valueStyle={{ color: '#0047bb' }}
                  precision={0}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm">
                <Statistic
                  title="Variación vs Periodo Anterior"
                  value={ingresosPorPeriodo?.comparacion?.variacionPorcentual || 0}
                  precision={1}
                  suffix="%"
                  prefix={
                    ingresosPorPeriodo?.comparacion?.tendencia === 'crecimiento' ? 
                      <RiseOutlined /> : 
                      ingresosPorPeriodo?.comparacion?.tendencia === 'decrecimiento' ?
                      <FallOutlined /> : null
                  }
                  valueStyle={{ 
                    color: ingresosPorPeriodo?.comparacion?.tendencia === 'crecimiento' ? 
                      '#0047bb' : 
                      ingresosPorPeriodo?.comparacion?.tendencia === 'decrecimiento' ?
                      '#ff4d4f' : '#8c8c8c'
                  }}
                />
                <Divider className="my-2" />
                <Text type="secondary" className="text-xs capitalize">
                  {ingresosPorPeriodo?.comparacion?.tendencia || 'estable'}
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Tabs */}
          <Card className="shadow-sm">
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              {/* Tab Tendencia */}
              <Tabs.TabPane
                tab={
                  <span>
                    <LineChartOutlined />
                    Tendencia
                  </span>
                }
                key="resumen"
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <div style={{ height: '400px' }}>
                      {prepararDatosTendencia() ? (
                        <Line data={prepararDatosTendencia()} options={opcionesGraficoLinea} />
                      ) : (
                        <Empty description="No hay datos para mostrar" />
                      )}
                    </div>
                  </Col>
                </Row>
              </Tabs.TabPane>

              {/* Tab Talleres */}
              <Tabs.TabPane
                tab={
                  <span>
                    <BarChartOutlined />
                    Por Talleres
                  </span>
                }
                key="talleres"
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={14}>
                    <div className="mb-4 flex justify-between items-center">
                      <AntTitle level={5}>Top 20 Talleres por Ingresos</AntTitle>
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => exportarCSV(ingresosPorTaller?.topTalleres || [], 'ingresos_talleres')}
                      >
                        Exportar CSV
                      </Button>
                    </div>
                    <Table
                      columns={columnasTalleres}
                      dataSource={ingresosPorTaller?.topTalleres || []}
                      rowKey={(record) => record.tallerId}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: 800 }}
                    />
                  </Col>

                  <Col xs={24} lg={10}>
                    <AntTitle level={5} className="mb-4">Top 10 Visualizado</AntTitle>
                    <div style={{ height: '500px' }}>
                      {prepararDatosTalleres() ? (
                        <Bar data={prepararDatosTalleres()} options={opcionesGraficoBarra} />
                      ) : (
                        <Empty description="No hay datos para mostrar" />
                      )}
                    </div>
                  </Col>
                </Row>
              </Tabs.TabPane>

              {/* Tab Recintos */}
              <Tabs.TabPane
                tab={
                  <span>
                    <ShopOutlined />
                    Por Recintos
                  </span>
                }
                key="recintos"
              >
                <div className="mb-4 flex justify-between items-center">
                  <AntTitle level={5}>Ingresos por Recinto</AntTitle>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => exportarCSV(ingresosPorRecinto || [], 'ingresos_recintos')}
                  >
                    Exportar CSV
                  </Button>
                </div>
                <Table
                  columns={columnasRecintos}
                  dataSource={ingresosPorRecinto || []}
                  rowKey={(record) => record.recintoId}
                  pagination={{ pageSize: 10 }}
                />
              </Tabs.TabPane>

              {/* Tab Planes */}
              <Tabs.TabPane
                tab={
                  <span>
                    <CalendarOutlined />
                    Por Tipo de Plan
                  </span>
                }
                key="planes"
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <AntTitle level={5} className="mb-4">Distribución de Ingresos</AntTitle>
                    <div style={{ height: '400px' }}>
                      {prepararDatosPlanes() ? (
                        <Pie data={prepararDatosPlanes()} options={opcionesGraficoPie} />
                      ) : (
                        <Empty description="No hay datos para mostrar" />
                      )}
                    </div>
                  </Col>

                  <Col xs={24} lg={12}>
                    <AntTitle level={5} className="mb-4">Detalle por Plan</AntTitle>
                    <div className="space-y-3">
                      {ingresosPorPlan?.distribucion?.map((plan, index) => (
                        <Card key={index} size="small" className="bg-gray-50">
                          <Row gutter={16} align="middle">
                            <Col span={8}>
                              <Text strong className="capitalize">{plan.plan}</Text>
                            </Col>
                            <Col span={8} className="text-center">
                              <Statistic
                                value={plan.cantidadInscripciones}
                                suffix="inscripciones"
                                valueStyle={{ fontSize: '16px' }}
                              />
                            </Col>
                            <Col span={8} className="text-right">
                              <Text strong style={{ color: '#0047bb', fontSize: '18px' }}>
                                {formatearMoneda(plan.totalIngresos)}
                              </Text>
                              <br />
                              <Text type="secondary" className="text-xs">
                                Prom: {formatearMoneda(plan.ingresoPromedio)}
                              </Text>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </div>
                  </Col>
                </Row>
              </Tabs.TabPane>
            </Tabs>
          </Card>
        </>
      )}
    </div>
  );
};

export default IngresosVentas;
